import { DecorationStyle, HeaderPositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

const SHAPE_PRESETS = {
    slope: {
        points: [{ x: 0, y: 0, hx: 0.36, hy: 0 }, { x: 1, y: 1, hx: 0.85, hy: 0 }],
        mirror: false,
        straightLines: false,
    },
    ramp: {
        points: [{ x: 0, y: 1, hx: 0.8, hy: 0 }, { x: 1, y: 0, hx: 0, hy: 0 }],
        mirror: false,
        straightLines: false,
    },
    bump: {
        points: [{ x: 0, y: 1, hx: 0.25, hy: 0 }, { x: 0.5, y: 0.1, hx: 0.25, hy: 0 }, { x: 1, y: 1, hx: 0.25, hy: 0 }],
        mirror: false,
        straightLines: false,
    },
    funnel: {
        points: [{ x: 0, y: 0, hx: 0.36, hy: 0 }, { x: 1, y: 1, hx: 0.85, hy: 0 }],
        mirror: true,
        straightLines: true,
    },
    wedge: {
        points: [{ x: 0, y: 1, hx: 0.25, hy: 0 }, { x: 0.5, y: 0.1, hx: 0.25, hy: 0 }, { x: 1, y: 1, hx: 0.25, hy: 0 }],
        mirror: true,
        straightLines: true,
    },
    bowtie: {
        points: [{ x: 0, y: 0, hx: 0.3, hy: 0 }, { x: 0.5, y: 0.75, hx: 0.2, hy: 0 }, { x: 1, y: 0, hx: 0.3, hy: 0 }],
        mirror: true,
        straightLines: false,
    },
};

const SHAPE_PATTERNS = [
    { type: "bowtie", pattern: /\b(bow[\s-]?tie)\b/i },
    { type: "wedge", pattern: /\b(wedge)\b/i },
    { type: "bump", pattern: /\b(bump|rise[\s-]+and[\s-]+fall|bell[\s-]+curve|normal distribution)\b/i },
    { type: "ramp", pattern: /\b(ramp)\b/i },
    { type: "slope", pattern: /\b(slope)\b/i },
    { type: "funnel", pattern: /\b(horizontal[\s-]?funnel|mirrored funnel|continuous funnel|funnel)\b/i },
];

export class HorizontalFunnelCategory extends BaseCategory {
    get name() {
        return "Horizontal Funnel";
    }

    get type() {
        return SlideCategories.HORIZONTAL_FUNNEL;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for slope, ramp, bump, horizontal funnel, wedge, or bowtie visuals that show 3-10 continuously related stages, categories, or values across a changing profile. Prefer this over comparison slides when the items are part of one ordered continuum rather than a side-by-side comparison.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt(options = {}) {
        return `Create a horizontal funnel style slide.
        Use 3-10 ordered items that belong on one continuous slope, ramp, bump, funnel, wedge, or bowtie shape.
        Use the bump shape for rise and fall, bell curve, or normal distribution style requests.
        Each item may include a short in-shape value/label plus an optional section title and description below the shape.
        Set preferredShape to slope, ramp, bump, funnel, wedge, or bowtie based on the user's wording.`;
    }

    getPromptSchema(options = {}) {
        return {
            items: [{
                value: options.preserveExactText ? "the exact in-shape value, number, or primary label for this section" : "short in-shape value, number, or primary label for this section",
                title: options.preserveExactText ? "the exact optional section title shown below the shape" : "optional short section title shown below the shape",
                description: options.preserveExactText ? "the exact optional short description for the section" : "optional short description for the section",
            }],
            preferredShape: "slope | ramp | bump | funnel | wedge | bowtie",
            startLabel: "optional short label at the start of the shape",
            endLabel: "optional short label at the end of the shape",
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getImagePrompt() {
        return false;
    }

    validateGeneratedModel(model) {
        return this.normalizeItems(model).length > 0;
    }

    getSlideModels(model, options = {}) {
        const normalized = this.normalizeModel(model);

        return [
            this.buildHorizontalFunnelModel(normalized, options),
            this.buildFunnelFallbackModel(normalized, options),
            this.buildPyramidFallbackModel(normalized, options),
        ];
    }

    normalizeModel(model = {}) {
        const items = this.normalizeItems(model);
        const shapeType = this.detectShapeType(model);
        const showSectionDescriptions = items.some(item => item.title || item.description);

        return {
            ...model,
            slideTitle: `${model?.slideTitle || "Horizontal Funnel"}`.trim() || "Horizontal Funnel",
            items,
            shapeType,
            showSectionDescriptions,
            startLabel: `${model?.startLabel || ""}`.trim(),
            endLabel: `${model?.endLabel || ""}`.trim(),
            conclusion: `${model?.conclusion || model?.summaryLine || ""}`.trim(),
        };
    }

    normalizeItems(model = {}) {
        const rawItems = model?.items ?? model?.sections ?? model?.categories ?? model?.steps ?? [];
        return (Array.isArray(rawItems) ? rawItems : [])
            .map(item => this.normalizeItem(item))
            .filter(item => item.value || item.title || item.description)
            .slice(0, 10);
    }

    normalizeItem(item) {
        if (typeof item === "string") {
            const value = item.trim();
            return {
                value,
                title: "",
                description: "",
            };
        }

        const value = `${item?.value ?? item?.metric ?? item?.label ?? item?.title ?? item?.name ?? item?.text ?? item?.stage ?? item?.category ?? ""}`.trim();
        let title = `${item?.title ?? item?.label ?? item?.name ?? item?.stage ?? item?.category ?? ""}`.trim();
        const description = `${item?.description ?? item?.details ?? item?.detail ?? item?.notes ?? ""}`.trim();

        if (title === value && !description) {
            title = "";
        }

        return {
            value,
            title,
            description,
        };
    }

    detectShapeType(model = {}) {
        const explicitShape = `${model?.preferredShape || model?.shapeType || ""}`.trim().toLowerCase();
        if (SHAPE_PRESETS[explicitShape]) {
            return explicitShape;
        }

        const textCandidates = [model.slideTitle, model.userPrompt, model.prompt]
            .filter(value => typeof value === "string" && value.trim().length > 0);

        for (const text of textCandidates) {
            for (const { type, pattern } of SHAPE_PATTERNS) {
                if (pattern.test(text)) {
                    return type;
                }
            }
        }

        return "funnel";
    }

    createHorizontalFunnelItems(items, builder, showSectionDescriptions) {
        return items.map(item => ({
            value: {
                blocks: [
                    builder.createBlock(item.value || item.title || "Stage", TextStyleType.TITLE),
                ].filter(Boolean)
            },
            text: showSectionDescriptions ? {
                blocks: [
                    builder.createBlock(item.title, TextStyleType.TITLE),
                    builder.createBlock(item.description, TextStyleType.BODY),
                ].filter(Boolean)
            } : undefined,
        }));
    }

    createClassicFunnelItems(items, builder) {
        return items.map(item => ({
            text: {
                blocks: [
                    builder.createBlock(item.title || item.value || "Stage", TextStyleType.TITLE),
                    builder.createBlock(item.description || (!item.title ? "" : item.value), TextStyleType.BODY),
                ].filter(Boolean)
            }
        }));
    }

    buildHorizontalFunnelModel(model, options = {}) {
        const builder = new SlideModelBuilder("horizontal-funnel", model, true);
        const preset = SHAPE_PRESETS[model.shapeType] ?? SHAPE_PRESETS.funnel;

        builder.createHeader({
            heading: model.slideTitle,
            position: HeaderPositionType.TOP,
        });

        builder.createPrimary({
            ornament: "number",
            collectionColor: "theme-to-dark",
            showSectionDescriptions: model.showSectionDescriptions,
            showValues: true,
            funnel: preset.points,
            mirror: preset.mirror,
            straightLines: preset.straightLines,
            items: this.createHorizontalFunnelItems(model.items, builder, model.showSectionDescriptions),
            startLabel: model.startLabel ? builder.createSingleBlockText(model.startLabel, "_use_styles_") : undefined,
            endLabel: model.endLabel ? builder.createSingleBlockText(model.endLabel, "_use_styles_") : undefined,
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }

    buildFunnelFallbackModel(model, options = {}) {
        const builder = new SlideModelBuilder("funnel", model);

        builder.createHeader({
            heading: model.slideTitle,
            position: HeaderPositionType.TOP,
        });

        builder.createPrimary({
            collectionColor: "colorful",
            baseSize: 47,
            capSize: 5,
            capAlign: "center",
            decorationStyle: DecorationStyle.FILLED,
            items: this.createClassicFunnelItems(model.items, builder),
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }

    buildPyramidFallbackModel(model, options = {}) {
        const builder = new SlideModelBuilder("pyramid", model);

        builder.createHeader({
            heading: model.slideTitle,
            position: HeaderPositionType.TOP,
        });

        builder.createPrimary({
            collectionColor: "theme-to-dark",
            baseSize: 47,
            capSize: 5,
            capAlign: "center",
            decorationStyle: DecorationStyle.FILLED,
            items: this.createClassicFunnelItems(model.items, builder),
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }
}
